import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { esES } from '@mui/x-data-grid/locales'
/**
 * @module CommonComponents
 */

/**
 * Callback executed when the page change
 * @callback CustomTableOnPageChange
 * @param {import('@mui/x-data-grid').GridPaginationModel} model
 * @param {import('@mui/x-data-grid').GridCallbackDetails<any>} model
 * @returns {void}
 */

/**
 * A reusable table component using MUI DataGrid
 * @param {Object} config The react props
 * @param {Array<Object>} config.dataSource,
 * @param {string} [config.rowid] the field name to get the rowid
 * @param {boolean} [config.checkbox] the table will have a checkbox
 * @param {boolean} [config.selectOnClick] The user can select on click
 * @param {number} [config.initialPageSize] Initial Page Size
 * @param {boolean} [config.hideFooterPagination] Hide the footer pagination
 * @param {boolean}  [config.hideFooter] Hide the footer
 * @param {onClickEvent} [config.onClickEvent] On Click Callback Send (params, event, details)
 * @param {Function} [config.onRowDoubleClick] On Row Double Click Callback Send (params, event, details)
 * @param {Object} [config.headerChilds]
 * @param {Array<CustomTableGridOptions>} config.fields - The options for the columns
 * @param {boolean} [config.showToolbar] Show the table toolbar
 * @param {boolean} [config.showQuickFilter] Show the search input on toolbar
 * @param {import('@mui/x-data-grid').GridDensity} [config.density='compact'] - The default grid density
 * @param {boolean} [config.loading=undefined] - Is loading the table
 * @param {boolean} [config.hideHeader=false] - Hide the table header
 * @param {Function} [config.handleContextMenu] - Callback to execute on right click
 * @param {string} [config.height='100%'] - The heignt of the table default (100%)
 * @param {import('@mui/material').SxProps} [config.sx] - Extra sx props
 * @param {CustomTableOnPageChange} [config.onPageChange] - Callback triggered when the page is changed
 * @param {boolean} [config.autoHeight=false]
 * @param {boolean} [config.hideColumnFilter] - Oculta el boton de filtros
 * @param {Function} [config.onSelectionChange] - Callback to send all selected ids
 * @param {Array<number|string>} [config.rowSelectionModel] - Array of selected row ids
 * @return {ReactJSXElement}
 * */
const CustomTable = ({
  dataSource,
  autoHeight = true,
  fields,
  rowid = 'id',
  checkbox = false,
  selectOnClick = false,
  initialPageSize = 10,
  hideFooterPagination = false,
  loading = undefined,
  hideFooter = false,
  headerChilds = null,
  showToolbar = false,
  showQuickFilter = true,
  hideColumnFilter = false,
  hideHeader = false,
  density = 'standard',
  handleContextMenu = null,
  height,
  sx = {},
  onClickEvent = (_, __, ___) => {
    return
  },
  onRowDoubleClick = (_, __, ___) => {
    return
  },
  onPageChange = (a) => {},
  customToolbar = null,
  onSelectionChange = (ids) => {
    return
  },
  rowSelectionModel = { type: 'include', ids: new Set() },
  getRowClassName = null,
  isRowSelectable = () => true,
}) => {
  /** @type {Array<import('@mui/x-data-grid').GridColDef<MyUserDto>>} */
  const columns = fields.map(
    ({
      value,
      key,
      width,
      sortable,
      flex,
      filterable,
      renderCell,
      type,
      customGetter,
      disableColumnMenu,
      valueOptions,
      hide,
    }) => {
      /** @type {import('@mui/x-data-grid').GridColDef} */
      const column = {
        field: value,
        headerName: key,
        sortable: sortable ?? false,
        filterable: filterable ?? false,
        type: type ?? 'string',
        disableColumnMenu: disableColumnMenu ?? false,
        hide: hide ?? false,
      }

      if (valueOptions) column.valueOptions = valueOptions

      if (width) {
        column.width = width
        column.minWidth = width
      }
      if (flex) column.flex = flex
      if (customGetter) column.valueGetter = customGetter

      if (renderCell)
        column.renderCell = (params) => {
          return renderCell(params.row)
        }

      return column
    },
  )

  const rowSlotProp = handleContextMenu
    ? {
        row: {
          onContextMenu: handleContextMenu,
          style: { cursor: 'context-menu' },
        },
      }
    : {}

  const columnVisibilityModel = columns.reduce((acc, col) => {
    if (col.hide) {
      acc[col.field] = false
    }
    return acc
  }, {})

  return (
    <div className="custom-table" style={{ height }}>
      {headerChilds}
      <DataGrid
        autoHeight={autoHeight}
        loading={loading && dataSource.length === 0}
        disableColumnFilter={hideColumnFilter}
        columnHeaderHeight={hideHeader ? 0 : 56}
        sx={{
          border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            path: {
              color: 'text.primary',
              opacity: '.7',
            },
            backgroundColor: 'transparent',
            color: 'text.primary',
            pb: 0,
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: 'background.paper',
            borderRadius: 3,
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            position: 'relative',
            transition: '.3s',
            '&:hover': {
              backgroundColor: 'background.success',
            },
          },
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: 'fit-content',
            maxHeight: '300px',
            height: '100px',
            transition: '.3s',
            '& .MuiDataGrid-overlay': {
              transition: '.3s',
              padding: '20px',
              userSelect: 'none',
              fontSize: '1.5em',
              color: 'text.disabled',
            },
          },
          '& .MuiDataGrid-cell': {
            borderColor: 'divider',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-toolbarContainer': {
            padding: '5px 0px',
            '& .MuiButtonBase-root': {
              color: 'text.primary',
            },
          },
          '& .row-disabled': {
            opacity: 0.45,
            backgroundColor: 'action.disabledBackground',
            cursor: 'not-allowed',
          },
          '& .MuiDataGrid-virtualScrollerContent': {
            mb: 0,
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
            display: 'none',
          },

          ...sx,
        }}
        onRowClick={(params, event, details) => {
          onClickEvent(params, event, details)
        }}
        onRowDoubleClick={(params, event, details) => {
          onRowDoubleClick(params, event, details)
        }}
        slots={{ toolbar: showToolbar ? (customToolbar ? customToolbar : GridToolbar) : null }}
        slotProps={{
          toolbar: {
            showQuickFilter: showQuickFilter,
            quickFilterProps: { debounceMs: 500 },
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
          },
          ...rowSlotProp,
        }}
        rows={dataSource}
        columns={columns}
        density={density}
        disableDensitySelector={true}
        localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        getRowId={(row) => row[rowid]}
        hideFooter={hideFooter}
        initialState={{
          filter: {
            filterModel: {
              items: [],
            },
          },
          pagination: {
            paginationModel: { page: 0, pageSize: initialPageSize },
          },
          columns: {
            columnVisibilityModel,
          },
        }}
        hideFooterPagination={hideFooterPagination}
        pageSizeOptions={[
          Math.ceil(initialPageSize / 2),
          initialPageSize,
          Math.ceil(initialPageSize * 1.5),
          Math.ceil(initialPageSize * 2),
        ]}
        rowSelection={checkbox || selectOnClick}
        disableRowSelectionExcludeModel
        checkboxSelection={checkbox}
        disableRowSelectionOnClick={!selectOnClick}
        onPaginationModelChange={onPageChange}
        onRowSelectionModelChange={(model) => onSelectionChange([...model.ids])}
        rowSelectionModel={
          Array.isArray(rowSelectionModel)
            ? { type: 'include', ids: new Set(rowSelectionModel) }
            : rowSelectionModel
        }
        getRowClassName={getRowClassName}
        isRowSelectable={isRowSelectable}
      />
    </div>
  )
}

export default CustomTable
